// Pool d'invités neutres — visages génériques, jamais liés à un métier.
// Le portrait est choisi de façon stable par hash de la key `expert-<uuid8>`,
// pour que le même invité conserve le même visage toute la séance.

import g01 from "@/assets/landing-v3/invites/g-01.jpg";
import g02 from "@/assets/landing-v3/invites/g-02.jpg";
import g03 from "@/assets/landing-v3/invites/g-03.jpg";
import g04 from "@/assets/landing-v3/invites/g-04.jpg";
import g05 from "@/assets/landing-v3/invites/g-05.jpg";

export const GUEST_PORTRAITS: ReadonlyArray<string> = [g01, g02, g03, g04, g05];

export function isGuestKey(key: string | null | undefined): boolean {
  return typeof key === "string" && key.toLowerCase().startsWith("expert-");
}

function hashString(s: string): number {
  // FNV-1a 32-bit — déterministe, stable d'une session à l'autre.
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

export function guestPortraitFor(key: string): string {
  return GUEST_PORTRAITS[hashString(key.toLowerCase()) % GUEST_PORTRAITS.length];
}

export interface GuestInfo {
  /** "expert-<uuid8>" — clé d'identité stable côté backend. */
  key: string;
  /** Rôle affiché en ligne-nom, ex. "Maître parfumeur". Jamais un prénom. */
  title: string;
  /** Domaine court optionnel, ex. "Parfumerie". */
  domain?: string;
  /** Accent couleur si fourni par le backend. */
  colorHex?: string;
  /** Portrait du pool (assigné par hash de la key). */
  portrait: string;
}

function pickString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (typeof c === "string") {
      const t = c.trim();
      if (t.length > 0) return t;
    }
  }
  return undefined;
}

/**
 * Construit un GuestInfo depuis un payload SSE `expert_joined`.
 * Le payload peut être plus ou moins riche selon le backend, on est tolérant.
 */
export function guestFromPayload(payload: Record<string, unknown>): GuestInfo | null {
  const rawKey = pickString(payload.key, payload.id, payload.persona, payload.expertKey);
  if (!rawKey || !isGuestKey(rawKey)) return null;
  const key = rawKey.toLowerCase();
  const title = pickString(payload.title, payload.role, payload.name) ?? "Invité";
  const domain = pickString(payload.domain, payload.field, payload.specialty);
  const colorHex = pickString(payload.colorHex, payload.color, payload.accent);
  return {
    key,
    title,
    domain,
    colorHex,
    portrait: guestPortraitFor(key),
  };
}
